import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { CSSProperties } from "styled-components";
import { useWindowSize } from "../../hooks/useWindowSize";
import { AspectRatioContainer } from "./styles";

interface ComponentProps {
  width?: number | string; // Ancho para videos wide (se usará directamente, por defecto 640)
  isPortrait?: boolean; // Orientación del contenido (opcional, si no se proporciona se detecta automáticamente)
  children;
  style?: CSSProperties;
  className?: string; // Prop para clases CSS adicionales
}

const Component = ({ children, width: providedWidth, isPortrait: providedIsPortrait, className, ...rest }: ComponentProps) => {
  const { isMobile } = useWindowSize()
  const [detectedIsPortrait, setDetectedIsPortrait] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Usar la orientación proporcionada o la detectada automáticamente
  const isPortrait = providedIsPortrait !== undefined ? providedIsPortrait : detectedIsPortrait;

  /**
   * Detecta la orientación del contenido (portrait/wide)
   * Maneja videos HTML5, iframes (YouTube, Vimeo), imágenes y otros elementos
   */
  const detectContentOrientation = useCallback(() => {
    if (!containerRef.current) return;

    // Buscar video HTML5
    const videoElement = containerRef.current.querySelector('video') as HTMLVideoElement;
    if (videoElement) {
      // Para videos HTML5 - usar dimensiones intrínsecas
      if (videoElement.videoWidth && videoElement.videoHeight) {
        setDetectedIsPortrait(videoElement.videoHeight > videoElement.videoWidth);
        return;
      }
    }

    // Buscar iframe (YouTube, Vimeo, etc.)
    const iframeElement = containerRef.current.querySelector('iframe') as HTMLIFrameElement;
    if (iframeElement) {
      // Método 1: Usar atributos width/height del iframe
      const iframeWidth = iframeElement.getAttribute('width');
      const iframeHeight = iframeElement.getAttribute('height');
      if (iframeWidth && iframeHeight) {
        const width = parseInt(iframeWidth);
        const height = parseInt(iframeHeight);
        if (width > 0 && height > 0) {
          setDetectedIsPortrait(height > width);
          return;
        }
      }

      // Método 2: Usar dimensiones del iframe renderizado (más confiable)
      const rect = iframeElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Solo usar si las dimensiones son razonables (no 0x0 o muy pequeñas)
        if (rect.width > 50 && rect.height > 50) {
          setDetectedIsPortrait(rect.height > rect.width);
          return;
        }
      }

      // Método 3: Usar dimensiones del contenido del iframe (si es accesible)
      try {
        const iframeDoc = iframeElement.contentDocument || iframeElement.contentWindow?.document;
        if (iframeDoc) {
          const body = iframeDoc.body;
          if (body) {
            const bodyRect = body.getBoundingClientRect();
            if (bodyRect.width > 0 && bodyRect.height > 0) {
              setDetectedIsPortrait(bodyRect.height > bodyRect.width);
              return;
            }
          }
        }
      } catch (e) {
        // Cross-origin iframe, no podemos acceder al contenido
      }
    }

    // Buscar imagen
    const imgElement = containerRef.current.querySelector('img') as HTMLImageElement;
    if (imgElement) {
      // Para imágenes - usar dimensiones naturales
      if (imgElement.naturalWidth && imgElement.naturalHeight) {
        setDetectedIsPortrait(imgElement.naturalHeight > imgElement.naturalWidth);
        return;
      }
    }

    // Buscar cualquier elemento con dimensiones (fallback)
    const anyElement = containerRef.current.querySelector('*') as HTMLElement;
    if (anyElement) {
      const rect = anyElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDetectedIsPortrait(rect.height > rect.width);
      }
    }
  }, []); // No tiene dependencias ya que solo usa containerRef.current

  /**
   * Calcula el ancho máximo basado en la orientación del contenido
   * - Videos portrait: width/2 (si se proporciona width) o 320px por defecto
   * - Videos wide: width (si se proporciona) o 640px por defecto
   * - Siempre con max-width: 100% para responsividad
   */
  const calculatedMaxWidth = useMemo(() => {
    const baseWidth = providedWidth || 640; // Por defecto 640px

    // Si baseWidth es un número, agregar "px", si es string dejarlo tal cual
    const baseWidthStr = typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth;

    // Para portrait, dividir entre 2
    const maxWidth = isPortrait ? `calc(${baseWidthStr} / 2)` : baseWidthStr;

    console.log('🚀 AspectRatio calculatedMaxWidth:', {
      baseWidth,
      baseWidthStr,
      isPortrait,
      maxWidth
    });
    return maxWidth;
  }, [providedWidth, isPortrait]);

  // Detectar orientación del contenido cuando el componente se monta o cambian los children
  useEffect(() => {
    // Esperar un poco para que el contenido se cargue
    const timer = setTimeout(() => {
      detectContentOrientation();
    }, 100);

    return () => clearTimeout(timer);
  }, [children]);

  // Detectar orientación cuando el contenido se carga
  useEffect(() => {
    if (!containerRef.current) return;

    const videoElement = containerRef.current.querySelector('video') as HTMLVideoElement;
    const iframeElement = containerRef.current.querySelector('iframe') as HTMLIFrameElement;
    const imgElement = containerRef.current.querySelector('img') as HTMLImageElement;

    // Para videos HTML5
    if (videoElement) {
      const handleLoadedMetadata = () => {
        detectContentOrientation();
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('loadeddata', handleLoadedMetadata);

      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('loadeddata', handleLoadedMetadata);
      };
    }

    // Para iframes (YouTube, Vimeo, etc.)
    if (iframeElement) {
      const startMeasurement = () => {

        // Medir el iframe de forma repetida durante 10 segundos
        let measurements: { width: number; height: number; timestamp: number }[] = [];
        const measurementInterval = 100; // Medir cada 100ms
        const totalDuration = 10000; // 10 segundos total
        let measurementCount = 0;
        const maxMeasurements = totalDuration / measurementInterval; // 100 mediciones

        const measureInterval = setInterval(() => {
          const rect = iframeElement.getBoundingClientRect();

          if (rect.width > 50 && rect.height > 50) {
            measurements.push({
              width: rect.width,
              height: rect.height,
              timestamp: Date.now()
            });
          } else {
            console.log(`⚠️ AspectRatio medición ${measurementCount + 1}: dimensiones inválidas`, {
              width: rect.width,
              height: rect.height
            });
          }

          measurementCount++;

          // Si hemos completado las mediciones, tomar la última medida válida
          if (measurementCount >= maxMeasurements) {
            clearInterval(measureInterval);

            if (measurements.length > 0) {
              const lastMeasurement = measurements[measurements.length - 1];
              const newIsPortrait = lastMeasurement.height > lastMeasurement.width;
              setDetectedIsPortrait(newIsPortrait);
            } else {
              console.log('❌ AspectRatio: No se obtuvieron mediciones válidas del iframe');
            }
          }
        }, measurementInterval);
      };

      const handleIframeLoad = () => {
        // Esperar un poco más para que el iframe se renderice completamente
        setTimeout(() => {
          startMeasurement();
        }, 500);
      };

      // Verificar si el iframe ya está cargado
      if (iframeElement.contentDocument) {
        startMeasurement();
      } else {
        // Solo usar el evento 'load' que es más confiable para iframes
        iframeElement.addEventListener('load', handleIframeLoad);
      }

      // También intentar detectar después de un tiempo fijo más largo
      const fallbackTimer = setTimeout(() => {
        startMeasurement();
      }, 3000); // Más tiempo para iframes

      return () => {
        iframeElement.removeEventListener('load', handleIframeLoad);
        clearTimeout(fallbackTimer);
      };
    }

    // Para imágenes
    if (imgElement) {
      const handleImageLoad = () => {
        detectContentOrientation();
      };

      imgElement.addEventListener('load', handleImageLoad);

      return () => {
        imgElement.removeEventListener('load', handleImageLoad);
      };
    }
  }, [children]);

  // Detección periódica para iframes que pueden tardar en cargar
  useEffect(() => {
    if (!containerRef.current) return;

    const iframeElement = containerRef.current.querySelector('iframe') as HTMLIFrameElement;
    if (!iframeElement) return;

    // Intentar detectar orientación periódicamente con intervalos más largos
    const periodicDetection = setInterval(() => {
      detectContentOrientation();
    }, 1000); // Intervalos más largos para iframes

    // Limpiar después de 15 segundos (más tiempo para iframes)
    const cleanupTimer = setTimeout(() => {
      clearInterval(periodicDetection);
    }, 15000);

    return () => {
      clearInterval(periodicDetection);
      clearTimeout(cleanupTimer);
    };
  }, [children]);

  return (
    <AspectRatioContainer
      ref={containerRef}
      className={`aspect-ratio ${isPortrait ? 'portrait' : ''} ${className || ''}`}
      style={{
        ...rest.style,
        maxWidth: calculatedMaxWidth,
        width: '100%' // Siempre 100% del contenedor padre
      }}
    >
      {children}
    </AspectRatioContainer>
  );
};

export default Component;
